import { useState, useEffect } from "react";
import { usersListUsers } from "../gen";
import type { FilterSelectOption } from "../components/common/FilterSelect";
import {
  CANONICAL_AGENT_LABELS,
  normalizeAgentType,
  sanitizeProtocoloName,
} from "./useUserDirectory";

export interface UseAgentOptionsConfig {
  includeUniversal?: boolean;
  includeFeedback?: boolean;
}

let cachedAgents: FilterSelectOption[] | null = null;
let fetchPromise: Promise<FilterSelectOption[]> | null = null;

async function fetchActiveAgents(): Promise<FilterSelectOption[]> {
  try {
    const res: any = await usersListUsers({ is_agent: true, limit: 100 });
    const rawUsers = res?.data?.users || res?.users || [];
    const map = new Map<string, string>();

    for (const u of rawUsers) {
      if (u.is_agent && u.agent_type && u.status !== "inactive") {
        const norm = normalizeAgentType(u.agent_type);
        let canonicalLabel =
          CANONICAL_AGENT_LABELS[norm] ||
          u.effective_name ||
          u.display_name ||
          norm;
        canonicalLabel = sanitizeProtocoloName(canonicalLabel, "Sofia");
        map.set(norm, canonicalLabel);
      }
    }

    const result: FilterSelectOption[] = Array.from(map.entries()).map(
      ([value, label]) => ({
        value,
        label,
      })
    );

    // Fallback if DB returned no agents (e.g. offline/mock environment)
    if (result.length === 0) {
      for (const [value, label] of Object.entries(CANONICAL_AGENT_LABELS)) {
        if (!map.has(value)) {
          result.push({ value, label });
        }
      }
    }

    cachedAgents = result;
    return result;
  } catch (err) {
    // If network fails and no cache exists, use canonical fallback
    if (!cachedAgents) {
      cachedAgents = Object.entries(CANONICAL_AGENT_LABELS).map(
        ([value, label]) => ({ value, label })
      );
    }
    return cachedAgents;
  }
}

export function useAgentOptions(
  config?: UseAgentOptionsConfig
): FilterSelectOption[] {
  const [agents, setAgents] = useState<FilterSelectOption[]>(() => cachedAgents || []);

  useEffect(() => {
    let mounted = true;

    if (!cachedAgents) {
      if (!fetchPromise) {
        fetchPromise = fetchActiveAgents();
      }
      fetchPromise
        .then((data) => {
          if (mounted) setAgents(data);
        })
        .finally(() => {
          fetchPromise = null;
        });
    } else {
      setAgents(cachedAgents);
    }

    return () => {
      mounted = false;
    };
  }, []);

  if (config?.includeUniversal) {
    return [{ value: "universal", label: "Universal (All Agents)" }, ...agents];
  }

  return agents;
}
